export const SESSION_TABLE_COLS = [
  { key: 'session_name', label: 'Track' },
  { key: 'date',         label: 'Date',     col: 'colDate' },
  { key: 'best_lap',     label: 'Best Lap', col: 'colBestLap' },
  { key: 'lap_count',    label: 'Laps',     col: 'colLaps' },
  { key: 'duration',     label: 'Duration', col: 'colDuration' },
  { key: 'racer',        label: 'Driver',   col: 'colDriver' },
  { key: 'vehicle',      label: 'Car',      col: 'colCar' },
  { key: 'video',        label: 'Video',    col: 'colVideo' },
  { key: null,           label: 'Weather',  col: 'colWeather' },
  { key: 'created_at',   label: 'Uploaded', col: 'colUploaded' },
  { key: null,           label: '',         col: 'colActions' },
];

export const SESSION_TAB_OPTIONS = [
  {
    key: 'all',
    label: 'All',
    detail: 'All sessions you can access, including your sessions and sessions shared with you.',
  },
  {
    key: 'private',
    label: 'My Sessions',
    detail: 'Sessions you uploaded and own.',
  },
  {
    key: 'shared',
    label: 'Shared',
    detail: 'Sessions other users have shared with you.',
  },
];

export const SESSION_SORT_COLS = new Set(
  SESSION_TABLE_COLS.map(column => column.key).filter(Boolean)
);

export function defaultSessionSortDirection(column) {
  return column === 'best_lap' ? 'asc' : 'desc';
}

export function nextSessionSort(current, column) {
  return current.col === column
    ? { col: column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    : { col: column, dir: defaultSessionSortDirection(column) };
}

export function sessionHasVideo(session) {
  return !!(session?.has_video || session?.has_rendered_video ||
    session?.has_original_video || session?.has_gopro_files);
}

export function comparisonGapSeconds(session, anchorBestSeconds) {
  const candidate = Number(session?.best_lap_seconds);
  const anchor = Number(anchorBestSeconds);
  if (!Number.isFinite(candidate) || !Number.isFinite(anchor) || anchor <= 0) return null;
  if (candidate > anchor * 2 || candidate < anchor * 0.5) return null;
  return candidate - anchor;
}

export function isComparisonSessionVisible(
  session,
  excludedSessionIds,
  isAdmin = false,
  adminModeActive = false,
) {
  if (!session || excludedSessionIds.has(session.session_id)) return false;
  if (!isAdmin || adminModeActive) return true;
  return !session.is_admin_view || !!session.shared_with_me;
}

export function isComparisonSessionSelectable(session) {
  return !!session?.can_reload;
}

export function formatSessionDuration(seconds) {
  if (!seconds) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}m ${String(remainder).padStart(2, '0')}s`;
}

export function formatSessionDateTimeCompact(isoStr) {
  if (!isoStr) return '';
  try {
    const date = new Date(isoStr);
    if (Number.isNaN(date.getTime())) return isoStr;
    const datePart = date.toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: '2-digit',
    });
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart} ${timePart}`;
  } catch {
    return isoStr;
  }
}

export function shortSessionYear(dateStr) {
  return typeof dateStr === 'string' ? dateStr.replace(/\/(\d{2})(\d{2})$/, '/$2') : dateStr;
}

export function formatSessionTime12(hhmm) {
  if (!hhmm) return '';
  const [hours, minutes] = hhmm.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

export function sessionWeatherIcon(condition) {
  if (!condition) return '🌡';
  const value = condition.toLowerCase();
  if (value.includes('rain')) return '🌧';
  if (value.includes('overcast') || value.includes('mostly cloudy')) return '☁';
  if (value.includes('partly')) return '⛅';
  return '☀';
}

export function sessionTrackDisplayName(session) {
  return (session && (session.canonical_track_name || session.session_name)) || null;
}

function pickerStorageKey(userId, trackKey) {
  return `ta_compareSessionFilters:${userId || 'current'}:${trackKey || 'unknown'}`;
}

const PICKER_FILTERS_VERSION = 1;
const TABS = new Set(SESSION_TAB_OPTIONS.map(option => option.key));

export function readCompareSessionFilters(storage, userId, trackKey) {
  try {
    const value = JSON.parse(storage.getItem(pickerStorageKey(userId, trackKey)) || 'null');
    if (!value || value.version !== PICKER_FILTERS_VERSION) return {};
    const sort = value.sort;
    return {
      filterText: typeof value.filterText === 'string' ? value.filterText : '',
      filterCar: typeof value.filterCar === 'string' ? value.filterCar : '',
      filterDriver: typeof value.filterDriver === 'string' ? value.filterDriver : '',
      tempRange: validRange(value.tempRange) ? value.tempRange : null,
      tempUnit: value.tempUnit === 'C' || value.tempUnit === 'F' ? value.tempUnit : null,
      dateField: value.dateField === 'uploaded' ? 'uploaded' : 'session',
      dateRange: validRange(value.dateRange) ? value.dateRange : null,
      datePreset: typeof value.datePreset === 'string' ? value.datePreset : null,
      conditions: ['all', 'dry', 'wet'].includes(value.conditions) ? value.conditions : 'all',
      activeTab: TABS.has(value.activeTab) ? value.activeTab : 'all',
      sort: sort && SESSION_SORT_COLS.has(sort.col) && ['asc', 'desc'].includes(sort.dir)
        ? sort
        : { col: 'date', dir: 'desc' },
      tableGrouped: value.tableGrouped !== false,
    };
  } catch {
    return {};
  }
}

export function writeCompareSessionFilters(storage, userId, trackKey, value) {
  try {
    storage.setItem(pickerStorageKey(userId, trackKey), JSON.stringify({
      version: PICKER_FILTERS_VERSION,
      ...value,
    }));
  } catch {
    // A locked-down browser may not expose sessionStorage. The picker remains
    // fully usable; it simply opens with broad defaults next time.
  }
}

export function validRange(value) {
  return Array.isArray(value) && value.length === 2 &&
    value.every(item => typeof item === 'number' && Number.isFinite(item)) &&
    value[0] <= value[1];
}

export function groupComparisonSessions(sessions, newestFirst = true) {
  const sorted = [...sessions].sort((a, b) => sessionDateMs(a) - sessionDateMs(b));
  const groups = [];
  for (const session of sorted) {
    const timestamp = sessionDateMs(session);
    if (!timestamp) {
      groups.push({ key: `single:${session.session_id}`, sessions: [session], start: 0, end: 0 });
      continue;
    }
    const group = groups.find(candidate =>
      candidate.start && Math.abs(timestamp - candidate.start) <= 3 * 86400000
    );
    if (group) {
      group.sessions.push(session);
      group.end = Math.max(group.end, timestamp);
    } else {
      groups.push({ key: `event:${timestamp}`, sessions: [session], start: timestamp, end: timestamp });
    }
  }
  const direction = newestFirst ? -1 : 1;
  groups.sort((a, b) => direction * (a.end - b.end));
  for (const group of groups) {
    group.sessions.sort((a, b) => direction * (sessionDateMs(a) - sessionDateMs(b)));
  }
  return groups;
}

export function formatComparisonGroupDate(group) {
  if (!group?.start) return 'No date';
  const start = new Date(group.start);
  const end = new Date(group.end || group.start);
  const options = { month: 'short', day: 'numeric', year: 'numeric' };
  if (group.start === group.end) return start.toLocaleDateString('en-US', options);
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${end.toLocaleDateString('en-US', options)}`;
}

function sessionDateMs(session) {
  const raw = session?.date || session?.created_at;
  if (!raw) return 0;
  const value = new Date(raw).getTime();
  return Number.isNaN(value) ? 0 : value;
}
